import React from 'react';
import type { ResolvedBrand } from '../types/project';
interface BrandLogoProps {
  brand: ResolvedBrand;
  size?: 'sm' | 'lg';
}

/**
 * Typographic recreations of each campaign's wordmark, drawn in code so they
 * inherit the brand palette instead of hotlinking remote assets.
 */
export function BrandLogo({
  brand,
  size = 'sm'
}: BrandLogoProps) {
  const lg = size === 'lg';
  switch (brand.logo) {
    case 'certifieddevs':
      return <span className="inline-flex items-center gap-2" data-id="element-10" data-mp="1yrsr9gycftfm">
          <svg viewBox="0 0 24 24" className={lg ? 'h-7 w-7' : 'h-5 w-5'} aria-hidden="true" fill={brand.accent} data-id="element-11" data-mp="1uss8tzjs2st5">
            <path d="M12 1.6l2.5 1.8 3-.3 1 2.9 2.6 1.6-.9 2.9.9 2.9-2.6 1.6-1 2.9-3-.3-2.5 1.8-2.5-1.8-3 .3-1-2.9L3.4 15l.9-2.9-.9-2.9L6 7.6l1-2.9 3 .3L12 1.6z" data-id="element-12" data-mp="23syitbg9pank" />
            <path d="M8.6 12.3l2.3 2.3 4.4-4.6" fill="none" stroke="#ffffff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" data-id="element-13" data-mp="ee0rcho675ym" />
          </svg>
          <span className={`font-extrabold tracking-tight ${lg ? 'text-3xl' : 'text-base'}`} style={{
          fontFamily: brand.displayFont,
          color: brand.text
        }} data-id="element-14" data-mp="1a3k9vdnf8ya0">
            Certified<span style={{
            color: brand.accent
          }} data-id="element-15" data-mp="1iwxbd3zjx87p">Devs</span>
          </span>
        </span>;
    case 'laravel':
      return <span className="inline-flex items-center gap-2.5" data-id="element-16" data-mp="1nv8iv82dj6mr">
          <span className={`inline-flex items-center justify-center ${lg ? 'h-10 w-10 rounded-xl' : 'h-7 w-7 rounded-lg'}`} style={{
          backgroundColor: brand.tint
        }} aria-hidden="true" data-id="element-17" data-mp="1subnwsictcuq">
            <svg viewBox="0 0 24 24" className={lg ? 'h-5 w-5' : 'h-4 w-4'} fill={brand.accent} data-id="element-18" data-mp="21uzuj25f4axl">
              <path d="M3 6.6l4.4 2.5v5l4.6 2.7 4.6-2.7v-5L21 6.6v7.9L12 19.8 3 14.5V6.6z" data-id="element-19" data-mp="3hhptmqj4ort" />
              <path d="M12 4.2l4.3 2.5L12 9.2 7.7 6.7 12 4.2z" opacity="0.55" data-id="element-20" data-mp="7ia1emmvj5r9" />
            </svg>
          </span>
          <span className={`font-bold leading-[1.05] ${lg ? 'text-2xl' : 'text-[13px]'}`} style={{
          fontFamily: brand.displayFont,
          color: brand.text
        }} data-id="element-21" data-mp="1asxtbh25dyqa">
            Certification
            <br data-id="element-22" data-mp="1jqztxvhyypd1" />
            for <span style={{
            color: brand.accent
          }} data-id="element-23" data-mp="1frimwxmw9k5r">Laravel</span>
          </span>
        </span>;
    case 'nexio':
      return <span className="inline-flex items-center gap-2.5" data-id="element-24" data-mp="1tq8keedmllun">
          <svg viewBox="0 0 32 32" className={lg ? 'h-9 w-9' : 'h-6 w-6'} aria-hidden="true" data-id="element-25" data-mp="1os6zrp0jxeh2">
            <rect x="3" y="2" width="9" height="28" fill="#4fd1c5" data-id="element-26" data-mp="23g1wrhlle4zy" />
            <path d="M12 2h8l9 28h-8L12 2z" fill={brand.accent} data-id="element-27" data-mp="1yi8lwb9cgmtc" />
            <path d="M20 2h9v28h-9V2z" fill={brand.text} data-id="element-28" data-mp="1yk39jxc02t14" />
          </svg>
          <span className="leading-none" data-id="element-29" data-mp="2cib3dwdqpe82">
            <span className={`block font-bold tracking-tight ${lg ? 'text-3xl' : 'text-base'}`} style={{
            fontFamily: brand.displayFont,
            color: brand.accent
          }} data-id="element-30" data-mp="27ioshtldwa2f">
              NEXIO
            </span>
            {lg && <span className="mt-1 block font-mono text-[9px] uppercase tracking-[0.18em]" style={{
            color: brand.textDim
          }} data-id="element-31" data-mp="2w31mck9bxsqg">
                Empowering community collaboration
              </span>}
          </span>
        </span>;
    case 'futura':
      return <span className="inline-flex items-center gap-2.5" data-id="element-32" data-mp="2d4p8wmbdxwp2">
          <svg viewBox="0 0 32 32" className={lg ? 'h-9 w-9' : 'h-6 w-6'} aria-hidden="true" stroke={brand.text} strokeWidth="1.4" fill="none" data-id="element-33" data-mp="2d6a4mf9frloi">
            <circle cx="16" cy="16" r="13" data-id="element-34" data-mp="23cji3fwvdlbg" />
            <ellipse cx="16" cy="16" rx="13" ry="5" data-id="element-35" data-mp="1pev0qi20gwb5" />
            <ellipse cx="16" cy="16" rx="13" ry="10" data-id="element-36" data-mp="1phh4s7pp0tom" />
            <path d="M16 3v26" data-id="element-37" data-mp="1ud6a6roe94su" />
          </svg>
          <span className={`tracking-[0.14em] ${lg ? 'text-2xl' : 'text-sm'}`} style={{
          fontFamily: brand.displayFont,
          color: brand.text
        }} data-id="element-38" data-mp="1ug0v0kbbkrms">
            <span className="font-bold" data-id="element-39" data-mp="1hf70z3klm39n">FUTURA</span>{' '}
            <span className="font-light" data-id="element-40" data-mp="1cmx413l2g2xv">SUMMIT</span>
          </span>
        </span>;
    case 'swi':
    default:
      return <span className="inline-flex items-center gap-2.5" data-id="element-41" data-mp="18lv40kjbrmuu">
          <svg viewBox="0 0 32 32" className={lg ? 'h-9 w-9' : 'h-6 w-6'} aria-hidden="true" data-id="element-42" data-mp="13nadjctvkhjl">
            <circle cx="16" cy="16" r="12" fill="none" stroke="#1273d4" strokeWidth="5" strokeDasharray="5 3" data-id="element-43" data-mp="ymmga9f1z4gx" />
            <circle cx="16" cy="16" r="6" fill="none" stroke="#1273d4" strokeWidth="1.6" data-id="element-44" data-mp="2o2vjpqar1n82" />
          </svg>
          <span className={`tracking-[0.16em] ${lg ? 'text-xl' : 'text-[11px]'}`} style={{
          fontFamily: brand.displayFont,
          color: brand.text
        }} data-id="element-45" data-mp="2k1a8sahrzj0i">
            SWEDISH WEALTH INSTITUTE
          </span>
        </span>;
  }
}